// libs
import React, { useState, memo, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';

// styles
import {
  Title,
  EcomPolicyCardWrapper,
  Container,
  PlaceHolder,
  EcomPolicyPlaceHolder,
} from './styles';

// components
import ContentWrapper from './components/ContentWrapper';
import PincodeServiceExp from 'shared/layouts/DetailsPage/components/ProductInfo/components/PinCodeServiceNewV2/PincodeServiceExp';
import PincodeService from 'shared/layouts/DetailsPage/components/ProductInfo/components/PinCodeServiceNewV2/PincodeService';
import EcomPolicyCard from './components/EcomPolicyCard';

// analytics
import { trackEcomPolicy } from './analytics';

// types
import { PincodeApiResponse } from 'shared/layouts/DetailsPage/components/ProductInfo/types';
import { PINCODE_ERROR_TEXT } from './constant';
import { ProductTypes } from 'shared/helpers/productHelpers';

// helpers
import { getPincodeInfo } from 'shared/layouts/DetailsPage/components/ProductInfo/storage';
import {
  getPinCodeToStorage,
  getIsServicable,
} from 'shared/layouts/DetailsPage/components/ProductInfo/Mobile/components/PinCodeOverlaySheet/helpers';

//hooks
import { useRemoteConfig } from 'shared/store/remoteConfigs/hooks';
import { useSelector } from 'react-redux';
import { isOneSize } from 'shared/store/details/selectors';
import useEDD from 'shared/components/EstimatedDeliveryDetails/hooks/useEdd';

// context
import { useDomainConfig } from 'shared/domainConfig/context/context';

// constants
const TITLE = 'Select Delivery Location';
const POLICY = ['cash_on_delivery', 'return_and_exchanges', 'delivery_details'];

interface IEcomPolicyItem {
  image: string;
  text: string;
  enable?: boolean;
}

interface IEcomPolicyProps {
  cashOnDelivery: IEcomPolicyItem;
  returnAndExchange: IEcomPolicyItem;
  deliveryDetails: IEcomPolicyItem;
  productId: number;
  setFetchEstimatedDeliveryDetails: () => void;
  inStockSKUs: string[];
  deliveryOrShippingText: string;
  isFetching: boolean;
  isEddEnabled: boolean;
}

function EcomPolicy({
  deliveryDetails,
  returnAndExchange,
  cashOnDelivery,
  productId,
  isFetching,
  inStockSKUs,
  deliveryOrShippingText,
  setFetchEstimatedDeliveryDetails,
  isEddEnabled,
}: IEcomPolicyProps) {
  const [showOverlaySheet, setShowOverlaySheet] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');
  const [isServicable, setIsServicable] = useState(true);
  const [apiResponse] = useState(getPinCodeToStorage());
  const [callServiceabilityAPI, setCallServiceabilityAPI] = useState(true);
  const [serviceabiltyResponse, setServiceabiltyResponse] = useState<PincodeApiResponse>({});
  const gatewayApiEnabled = useRemoteConfig('gatewayApiEnabled');
  const { fetchData } = useEDD();
  const [error, setError] = useState(null);
  const isConfigurableProduct = !useSelector(isOneSize);
  const [isFetchingEdd, setIsFetchingEdd] = useState(true);

  const {
    ECOM_STRIP_ICONS: { COD_AVAILABLE, RETURN_AND_EXCHANGE, FREE_DELIVERY },
    ECOM_STRIP_ALT: {
      FREE_DELIVERY: FREE_DELIVERY_ALT,
      COD_AVAILABLE: COD_AVAILABLE_ALT,
      RETURN_AND_EXCHANGE: RETURN_AND_EXCHANGE_ALT,
    },
  } = useDomainConfig();

  const onOverlayHide = () => {
    setShowOverlaySheet(false);
  };

  const handleClick = (enable: boolean, item: string) => {
    if (!enable) {
      return;
    }
    trackEcomPolicy(item);
    setShowOverlaySheet(true);
    setSelectedItem(item);
  };

  // deliveryOrShippingText is returned from serviceability api, so if we disable edd flag then deliveryOrShippingText
  // would be undefined and if serviceability api gives null data, for both cases will display delivery text using
  // deliveryDetails.text
  const policies = [
    {
      ...cashOnDelivery,
      alt: COD_AVAILABLE_ALT,
      image: COD_AVAILABLE,
    },
    {
      ...returnAndExchange,
      alt: RETURN_AND_EXCHANGE_ALT,
      image: RETURN_AND_EXCHANGE,
    },
    {
      ...deliveryDetails,
      image: FREE_DELIVERY,
      alt: FREE_DELIVERY_ALT,
      ...(deliveryOrShippingText && { text: deliveryOrShippingText }),
    },
  ];

  useEffect(() => {
    // edd feature flag experiment
    if (isEddEnabled) return;

    if (apiResponse) {
      setIsServicable(getIsServicable(apiResponse));
    }
  }, [apiResponse, isEddEnabled]);

  useEffect(() => {
    if (isFetching || isEmpty(productId)) {
      return;
    }
    // we only want to call serviceability api for the page load
    if (!isEddEnabled || callServiceabilityAPI === false) return;

    const { pincode } = getPincodeInfo();

    if (pincode !== '') {
      setIsFetchingEdd(true);
      setCallServiceabilityAPI(false);
      fetchData({
        pinCode: Number(pincode),
        SKUs: inStockSKUs,
        successCallback: (pincodeDetailsData) => {
          setIsFetchingEdd(false);
          const pinCodeDetailsData = pincodeDetailsData?.data;
          const pincodeService = pinCodeDetailsData?.pincodeDetails;
          const pincodeFetchData = pinCodeDetailsData?.skuDetails;
          if (pincodeService?.serviceable === true) {
            setFetchEstimatedDeliveryDetails(pincodeFetchData);

            setServiceabiltyResponse({ ...pincodeService, pincode });
            setIsServicable(true);
            return;
          } else {
            setIsServicable(false);
          }
        },
        failureCallback: () => {
          setIsServicable(false);
          setError(PINCODE_ERROR_TEXT);
          setIsFetchingEdd(false);
        },
        type: isConfigurableProduct ? ProductTypes.CONFIGURABLE : ProductTypes.SIMPLE,
      });
    } else {
      setCallServiceabilityAPI(false);
      setIsFetchingEdd(false);
    }
  }, [
    callServiceabilityAPI,
    setFetchEstimatedDeliveryDetails,
    isFetching,
    productId,
    inStockSKUs,
    isEddEnabled,
    gatewayApiEnabled,
    isConfigurableProduct,
    fetchData,
  ]);

  return (
    <Container data-test="dweb-ecom-policy">
      <Title>{TITLE}</Title>
      {isEddEnabled ? (
        <PincodeServiceExp
          isMobile
          setIsServiceable={setIsServicable}
          inStockSKUs={inStockSKUs}
          setFetchEstimatedDeliveryDetails={setFetchEstimatedDeliveryDetails}
          serviceabiltyResponse={serviceabiltyResponse}
          error={error}
          setError={setError}
          isConfigurableProduct={isConfigurableProduct}
          setIsFetchingEdd={setIsFetchingEdd}
        />
      ) : (
        <PincodeService isMobile setIsServiceable={setIsServicable} />
      )}
      {isFetchingEdd && (
        <PlaceHolder>
          <EcomPolicyPlaceHolder /> <EcomPolicyPlaceHolder /> <EcomPolicyPlaceHolder noMargin />{' '}
        </PlaceHolder>
      )}
      {isServicable && !isFetchingEdd && (
        <EcomPolicyCardWrapper data-at="ecom-strip">
          {policies.map((policy, index) => {
            if (!policy) {
              return null;
            }
            const name = POLICY[index];

            return (
              <EcomPolicyCard
                key={name}
                image={policy.image}
                text={policy.text}
                enable={policy.enable}
                alt={policy.alt}
                policyName={name}
                onClick={handleClick}
              />
            );
          })}

          {showOverlaySheet && (
            <ContentWrapper
              productId={productId}
              selectedItem={selectedItem}
              showOverlaySheet={showOverlaySheet}
              onOverlayHide={onOverlayHide}
              key={selectedItem}
            />
          )}
        </EcomPolicyCardWrapper>
      )}
    </Container>
  );
}

export default memo(EcomPolicy);
